import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ChevronLeft, TrendingUp, Users, FileText, DollarSign,
  Calendar, Clock, MessageCircle, RefreshCw, ArrowUp, ArrowDown,
  BarChart3, PieChart, Activity, Target, Instagram, AlertCircle, ChevronRight
} from 'lucide-react';
import AdminThemeToggle from '../components/AdminThemeToggle';
import AdminNotifications from '../components/AdminNotifications';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalytics = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [overviewRes, funnelRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/analytics/overview?period=${period}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/analytics/intake-funnel`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!overviewRes.ok) throw new Error('Failed to fetch analytics');
      
      const overviewData = await overviewRes.json();
      setData(overviewData);
      
      if (funnelRes.ok) {
        const funnel = await funnelRes.json();
        setFunnelData(funnel);
      }
    } catch (err) {
      console.error('Analytics error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getPackageLabel = (pkg) => {
    const labels = {
      'diagnostic-review': 'Diagnostic Review',
      'advocacy-launch': 'Advocacy Launch',
      'deep-dive': 'Deep Dive'
    };
    return labels[pkg] || pkg;
  };

  const getPackageColor = (pkg) => {
    const colors = {
      'diagnostic-review': '#3B82F6',
      'advocacy-launch': '#D4AF37',
      'deep-dive': '#4A0E4E'
    };
    return colors[pkg] || '#6B7280';
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">
          <RefreshCw className="spin" size={32} />
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Error Loading Analytics</h2>
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="admin-btn primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const trendData = data?.trend_data || [];
  const funnel = funnelData?.funnel || {};
  
  // Calculate max for trend chart
  const maxTrend = Math.max(...trendData.map(d => Math.max(d.intakes || 0, d.users || 0)), 5);

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <button onClick={() => navigate('/admin')} className="back-btn">
              <ChevronLeft size={20} />
              Back
            </button>
            <h1>Analytics Dashboard</h1>
            <p className="admin-subtitle">Business performance and insights</p>
          </div>
          <div className="admin-header-actions">
            <AdminThemeToggle />
            <AdminNotifications />
            <select 
              value={period} 
              onChange={(e) => setPeriod(e.target.value)}
              className="period-select"
            >
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last 90 Days</option>
              <option value="year">Last Year</option>
            </select>
            <button onClick={fetchAnalytics} className="admin-btn secondary">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

      {/* Key Metrics Cards */}
      <div className="analytics-metrics-grid">
        <Link to="/admin/intakes" className="analytics-metric-card highlight clickable" data-testid="revenue-card">
          <div className="metric-icon revenue">
            <DollarSign size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-value">{formatCurrency(metrics.estimated_revenue || 0)}</span>
            <span className="metric-label">Estimated Revenue</span>
          </div>
          <ChevronRight size={20} className="stat-arrow" />
        </Link>
        
        <Link to="/admin/intakes" className="analytics-metric-card clickable" data-testid="intakes-card">
          <div className="metric-icon intakes">
            <FileText size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-value">{metrics.total_intakes || 0}</span>
            <span className="metric-label">Total Intakes</span>
            <span className="metric-sub">+{metrics.period_intakes || 0} this period</span>
          </div>
          <ChevronRight size={20} className="stat-arrow" />
        </Link>
        
        <Link to="/admin/clients" className="analytics-metric-card clickable" data-testid="users-card">
          <div className="metric-icon users">
            <Users size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-value">{metrics.total_users || 0}</span>
            <span className="metric-label">Registered Users</span>
            <span className="metric-sub">+{metrics.new_users || 0} this period</span>
          </div>
          <ChevronRight size={20} className="stat-arrow" />
        </Link>
        
        <Link to="/admin/intakes" className="analytics-metric-card clickable" data-testid="conversion-card">
          <div className="metric-icon conversion">
            <Target size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-value">{metrics.conversion_rate || 0}%</span>
            <span className="metric-label">Conversion Rate</span>
            <span className="metric-sub">{metrics.completed_intakes || 0} completed</span>
          </div>
          <ChevronRight size={20} className="stat-arrow" />
        </Link>
      </div>

      {/* Charts Row */}
      <div className="analytics-charts-grid">
        {/* Trend Chart */}
        <div className="analytics-chart-card">
          <div className="chart-header">
            <h3><Activity size={18} /> Activity Trend (7 Days)</h3>
          </div>
          <div className="trend-chart">
            <div className="trend-bars">
              {trendData.map((day, idx) => (
                <div key={idx} className="trend-bar-group">
                  <div className="trend-bars-container">
                    <div 
                      className="trend-bar intakes"
                      style={{ height: `${(day.intakes / maxTrend) * 100}%` }}
                      title={`${day.intakes} intakes`}
                    >
                      <span className="bar-tooltip">{day.intakes}</span>
                    </div>
                    <div 
                      className="trend-bar users"
                      style={{ height: `${(day.users / maxTrend) * 100}%` }}
                      title={`${day.users} users`}
                    >
                      <span className="bar-tooltip">{day.users}</span>
                    </div>
                  </div>
                  <span className="trend-day">{day.day}</span>
                </div>
              ))}
            </div>
            <div className="trend-legend">
              <span className="legend-item"><span className="legend-dot intakes"></span> Intakes</span>
              <span className="legend-item"><span className="legend-dot users"></span> New Users</span>
            </div>
          </div>
        </div>

        {/* Package Distribution */}
        <div className="analytics-chart-card">
          <div className="chart-header">
            <h3><PieChart size={18} /> Package Distribution</h3>
          </div>
          <div className="package-chart">
            {Object.entries(metrics.package_breakdown || {}).map(([pkg, count]) => {
              const total = Object.values(metrics.package_breakdown || {}).reduce((a, b) => a + b, 0);
              const percent = total > 0 ? (count / total * 100).toFixed(0) : 0;
              
              return (
                <div key={pkg} className="package-bar-row">
                  <div className="package-info">
                    <span className="package-name">{getPackageLabel(pkg)}</span>
                    <span className="package-count">{count}</span>
                  </div>
                  <div className="package-bar-container">
                    <div 
                      className="package-bar-fill"
                      style={{ 
                        width: `${percent}%`,
                        backgroundColor: getPackageColor(pkg)
                      }}
                    ></div>
                  </div>
                  <span className="package-percent">{percent}%</span>
                </div>
              );
            })}
            {Object.keys(metrics.package_breakdown || {}).length === 0 && (
              <div className="no-data">No package data yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Funnel & Additional Stats */}
      <div className="analytics-bottom-grid">
        {/* Conversion Funnel */}
        <div className="analytics-chart-card funnel-card">
          <div className="chart-header">
            <h3><BarChart3 size={18} /> Intake Funnel</h3>
          </div>
          <div className="funnel-chart">
            {[
              { key: 'started', label: 'Started Intake', icon: FileText },
              { key: 'basic_info', label: 'Basic Info', icon: Users },
              { key: 'health_history', label: 'Health History', icon: Activity },
              { key: 'package_selected', label: 'Package Selected', icon: Target },
              { key: 'completed', label: 'Completed', icon: TrendingUp }
            ].map((step, idx, arr) => {
              const count = funnel[step.key] || 0;
              const maxCount = funnel.started || 1;
              const width = (count / maxCount * 100);
              const StepIcon = step.icon;
              const dropoff = idx > 0 ? 
                (((funnel[arr[idx-1].key] || 0) - count) / (funnel[arr[idx-1].key] || 1) * 100).toFixed(0) : 0;
              
              return (
                <div key={step.key} className="funnel-step">
                  <div className="funnel-step-header">
                    <StepIcon size={16} />
                    <span className="funnel-label">{step.label}</span>
                    <span className="funnel-count">{count}</span>
                    {idx > 0 && dropoff > 0 && (
                      <span className="funnel-dropoff">-{dropoff}%</span>
                    )}
                  </div>
                  <div className="funnel-bar-container">
                    <div 
                      className="funnel-bar-fill"
                      style={{ width: `${width}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="analytics-quick-stats">
          <div className="quick-stat-card">
            <div className="quick-stat-icon waitlist">
              <Users size={20} />
            </div>
            <div className="quick-stat-content">
              <span className="quick-stat-value">{metrics.total_waitlist || 0}</span>
              <span className="quick-stat-label">Waitlist Leads</span>
            </div>
          </div>
          
          <div className="quick-stat-card">
            <div className="quick-stat-icon bookings">
              <Calendar size={20} />
            </div>
            <div className="quick-stat-content">
              <span className="quick-stat-value">{metrics.upcoming_bookings || 0}</span>
              <span className="quick-stat-label">Upcoming Bookings</span>
            </div>
          </div>
          
          <div className="quick-stat-card">
            <div className="quick-stat-icon content">
              <Instagram size={20} />
            </div>
            <div className="quick-stat-content">
              <span className="quick-stat-value">{metrics.scheduled_content || 0}</span>
              <span className="quick-stat-label">Scheduled Posts</span>
            </div>
          </div>
          
          <div className="quick-stat-card">
            <div className="quick-stat-icon pending">
              <MessageCircle size={20} />
            </div>
            <div className="quick-stat-content">
              <span className="quick-stat-value">{metrics.pending_questions || 0}</span>
              <span className="quick-stat-label">Pending Inquiries</span>
            </div>
          </div>
          
          <div className="quick-stat-card">
            <div className="quick-stat-icon review">
              <Clock size={20} />
            </div>
            <div className="quick-stat-content">
              <span className="quick-stat-value">{metrics.pending_intakes || 0}</span>
              <span className="quick-stat-label">Intakes In Review</span>
            </div>
          </div>
          
          <div className="quick-stat-card">
            <div className="quick-stat-icon posted">
              <TrendingUp size={20} />
            </div>
            <div className="quick-stat-content">
              <span className="quick-stat-value">{metrics.posted_content || 0}</span>
              <span className="quick-stat-label">Posted Content</span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
